/**
 * Title-cases a person's name while preserving common UK naming conventions.
 *
 * Rules:
 *   - Each whitespace-separated word is capitalised: "john smith" → "John Smith"
 *   - Hyphens preserved: "mary-jane" → "Mary-Jane"
 *   - Apostrophes preserved: "o'brien" → "O'Brien", "d'angelo" → "D'Angelo"
 *   - "Mc" prefix: "mcdonald" → "McDonald"
 *   - "Mac" prefix (only for known long-form names, else left as capitalised
 *     first letter to avoid over-correcting names like "Mack" or "Mackey"):
 *     we keep it simple — just capitalise the first letter.
 *
 * Called on blur (not onChange) so the user's cursor position stays stable
 * while they're typing.
 */
export function titleCaseName(input) {
  if (!input || typeof input !== 'string') return input;

  const trimmed = input.trim();
  if (!trimmed) return trimmed;

  // Collapse internal whitespace to single spaces
  const normalised = trimmed.replace(/\s+/g, ' ');

  return normalised
    .split(' ')
    .map(word => capitaliseWord(word))
    .join(' ');
}

// Common company suffixes that should stay UPPERCASE.
// Also acronym-y bits that appear in company names.
const COMPANY_UPPERCASE = new Set([
  'LTD', 'LLP', 'PLC', 'LLC', 'CIC', 'LP', 'UK', 'GB',
  'PC', 'CC', 'HR', 'IT', 'AG', 'GMBH', 'BV', 'SA',
  'II', 'III', 'IV', 'VI', 'VII', 'VIII', 'IX', 'XI', 'XII',
  // Well-known organisations / brand acronyms
  'NHS', 'BBC', 'HSBC', 'DHL', 'BMW', 'VW', 'IBM', 'HMRC',
  'DVLA', 'BT', 'EE', 'O2', 'ITV', 'RSPCA', 'NSPCC', 'RBS',
  'TSB', 'HSBC', 'AA', 'RAC', 'ADT', 'BAE',
]);

// Small connector words in company names that should stay lowercase
// (unless first word).
const COMPANY_LOWERCASE = new Set(['and', 'of', 'the', 'for', 'to', '&']);

/**
 * Title-cases a company / dealership / business name, but keeps common
 * suffixes uppercase (Ltd → LTD, plc → PLC) and connector words lowercase
 * (Smith and Sons → Smith and Sons).
 *
 * Behaviour on all-caps SHOUTING input: title-cases everything (so "DERBY
 * TRADE CARS" → "Derby Trade Cars") — treats it as user shouting rather
 * than an all-acronym string. Individual short uppercase tokens embedded
 * in mixed-case input (like "BMW of London") are still preserved via the
 * COMPANY_UPPERCASE set.
 *
 * Examples:
 *   "acme ltd"          → "Acme LTD"
 *   "abc plc"           → "Abc PLC"
 *   "smith and sons"    → "Smith and Sons"
 *   "j.d. wetherspoon"  → "J.D. Wetherspoon"
 *   "DERBY TRADE CARS"  → "Derby Trade Cars"     (all-caps input → title-cased)
 *   "nhs"               → "NHS"                  (known acronym in map)
 *   "BMW of London"     → "BMW of London"        (short acronym preserved)
 */
export function titleCaseCompany(input) {
  if (!input || typeof input !== 'string') return input;
  const trimmed = input.trim();
  if (!trimmed) return trimmed;
  const normalised = trimmed.replace(/\s+/g, ' ');
  const words = normalised.split(' ');

  // If the whole string is SHOUTING (all letters uppercase), title-case
  // everything word-by-word — the user hasn't chosen to preserve any acronyms.
  const isAllUpper = /[A-Z]/.test(normalised) && normalised === normalised.toUpperCase();

  return words
    .map((word, i) => {
      const upper = word.toUpperCase();
      const lower = word.toLowerCase();
      // Lowercase connector words (except when it's the first word)
      if (i > 0 && COMPANY_LOWERCASE.has(lower)) return lower;
      // Keep known uppercase acronyms / suffixes uppercase
      if (COMPANY_UPPERCASE.has(upper)) return upper;
      // If original is a short mixed-case-input all-uppercase token (e.g.
      // "BMW" in "BMW of London"), preserve it — but NOT when the whole
      // string was SHOUTING (that's just poorly-cased data).
      if (!isAllUpper && word.length >= 2 && word.length <= 4
          && word === upper && /^[A-Z]+$/.test(word)) {
        return word;
      }
      return capitaliseWord(word);
    })
    .join(' ');
}

function capitaliseWord(word) {
  if (!word) return word;

  // Handle hyphenated names: split, capitalise each part, rejoin
  if (word.includes('-')) {
    return word.split('-').map(capitaliseWord).join('-');
  }

  // Handle apostrophes: "o'brien" → "O'Brien", "d'angelo" → "D'Angelo"
  if (word.includes("'")) {
    return word.split("'").map((part) => capitaliseWord(part)).join("'");
  }

  // Handle dotted initials like "j.d." → "J.D."
  if (word.includes('.')) {
    return word.split('.').map(capitaliseWord).join('.');
  }

  const lower = word.toLowerCase();

  // "Mc" prefix — capitalise the letter after Mc: "mcdonald" → "McDonald"
  if (lower.length > 2 && lower.startsWith('mc')) {
    return 'Mc' + lower.charAt(2).toUpperCase() + lower.slice(3);
  }

  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
